import type { LineSeries } from "@mui/x-charts-pro"
import type { WastewaterPCRActivityLevel } from "@phc/common/gen/threatAnalyticsGen"
import { useMemo } from "react"
import { dayjsUTC } from "../../../../utils/dates"
import { WASTEWATER_SHAPES } from "../../../Charts/legendUtils"
import { TimeSeriesChart } from "../../../Charts/TimeSeriesChart"
import {
  DEFAULT_SERIES_COLORS,
  type DatasetEntry,
} from "../../timeSeriesConstants"
import { fillWeeklyGaps } from "../../timeSeriesUtils"
import { type LegendItemData, WastewaterLegend } from "../../WastewaterLegend"
import { WastewaterTooltipContent } from "../../WastewaterTooltip"

interface WastewaterTimeSeriesProps {
  data?: WastewaterPCRActivityLevel[]
  valueKey?: keyof WastewaterPCRActivityLevel
  title?: string
  colors?: string[]
  locationName?: string
  anomalyKeys?: Set<string>
}

export const WastewaterTimeSeries = ({
  data,
  valueKey = "fecal_normalized_target_copies_per_l",
  title = "FECAL NORMALIZED TARGET (COPIES/L)",
  colors = DEFAULT_SERIES_COLORS,
  anomalyKeys,
}: WastewaterTimeSeriesProps) => {
  const { dataset, dates, series, legendItems } = useMemo(() => {
    if (!data || data.length === 0) {
      return {
        dataset: [],
        dates: [],
        series: [],
        legendItems: [],
      }
    }

    const targets = Array.from(new Set(data.map(d => d.target_name))).sort()
    // Identify date range
    const allDates = data
      .map(d => dayjsUTC(d.sampling_end_date))
      .sort((a, b) => a.valueOf() - b.valueOf())

    if (allDates.length === 0)
      return {
        dataset: [],
        dates: [],
        series: [],
        legendItems: [],
      }

    // Calculate Latest Analysis for Legend
    const latestAnalysisMap = new Map<string, WastewaterPCRActivityLevel>()
    data.forEach(d => {
      const existing = latestAnalysisMap.get(d.target_name)
      if (
        !existing ||
        dayjsUTC(d.sampling_end_date).isAfter(
          dayjsUTC(existing.sampling_end_date)
        )
      ) {
        latestAnalysisMap.set(d.target_name, d)
      }
    })

    const realSeries = targets.map(
      (target, i) =>
        ({
          id: target,
          dataKey: target,
          label: target,
          shape: WASTEWATER_SHAPES[i % WASTEWATER_SHAPES.length] ?? "circle",
          color: colors[i % colors.length] ?? "",
          showMark: true,
          curve: "linear" as const,
          connectNulls: false, // Ensure we show gaps for null values
          valueFormatter: (value: number | null) =>
            value !== null ? value.toExponential(2) : "N/A",
        }) satisfies LineSeries
    )

    const items: LegendItemData[] = realSeries.map(s => ({
      id: s.id,
      label: s.label,
      shape: s.shape,
      color: s.color,
      latestAnalysis: latestAnalysisMap.get(s.id),
    }))

    // Pre-group data by date for O(1) lookup efficiency
    const dateGroups = new Map<string, WastewaterPCRActivityLevel[]>()
    data.forEach(d => {
      const list = dateGroups.get(d.sampling_end_date) ?? []
      list.push(d)
      dateGroups.set(d.sampling_end_date, list)
    })

    const sortedEndDates = Array.from(dateGroups.keys()).sort()

    const { dataset: filledDataset, dates: denseDates } = fillWeeklyGaps(
      sortedEndDates,
      dateGroups,
      (dateObj, samples) => {
        const entry: DatasetEntry = { date: dateObj }
        // Initialize all targets to null for this date (explicit gap)
        targets.forEach(t => {
          entry[t] = null
        })

        // Fill in values from actual samples
        samples?.forEach(sample => {
          const val = sample[valueKey]
          if (sample.target_name) {
            // Store numeric value as-is (including 0 to show sample was taken with no detection)
            entry[sample.target_name] = val != null ? Number(val) : null
          }
        })

        return entry
      },
      dateObj => {
        const nullEntry: DatasetEntry = { date: dateObj }
        targets.forEach(t => {
          nullEntry[t] = null
        })
        return nullEntry
      }
    )

    return {
      dataset: filledDataset,
      dates: denseDates,
      series: realSeries,
      legendItems: items,
    }
  }, [data, valueKey, colors])

  return (
    <TimeSeriesChart
      dataset={dataset}
      dates={dates}
      series={series}
      legendItems={legendItems}
      yAxisLabel={title}
      renderLegend={({ items, hiddenIds, onToggle, onToggleAll }) => (
        <WastewaterLegend
          items={items}
          hiddenIds={hiddenIds}
          onToggle={onToggle}
          onToggleAll={onToggleAll}
          anomalyKeys={anomalyKeys}
        />
      )}
      renderTooltip={hiddenIds => (
        <WastewaterTooltipContent
          rawData={data}
          hiddenIds={hiddenIds}
          valueKey={valueKey}
          anomalyKeys={anomalyKeys}
        />
      )}
    />
  )
}
